import EventEmitter from 'eventemitter3';

import { __BLUEBELL_RESOURCE_MANAGER__, BLUEBELL_RESOURCE_MANAGER_VERSION } from './constant';
import { Resource, ResourceType } from './Resource';
import type { CrossOrigin } from './Def';

import { ProtectedEventEmitter } from './utils/ProtectedEventEmitter';
import { createGlobalFactory } from './utils/createGlobalFactory';
import { Debugger } from './utils/Debugger';

export interface ResourceManagerEvent {
    resourcecreated: { resource: Resource };
}

class ResourceManager extends Debugger implements ProtectedEventEmitter<ResourceManagerEvent> {
    readonly #resources: Map<string, Resource> = new Map();

    static #instance: ResourceManager;

    readonly #eventBus = new EventEmitter<keyof ResourceManagerEvent>();

    protected get debugName(): string {
        return 'resource-manager';
    }

    public off<T extends keyof ResourceManagerEvent>(
        event: T,
        listener: (ev: ResourceManagerEvent[T]) => unknown,
        context?: unknown
    ): this {
        this.#eventBus.off(event, listener, context);
        return this;
    }

    public on<T extends keyof ResourceManagerEvent>(
        event: T,
        listener: (ev: ResourceManagerEvent[T]) => unknown,
        context?: unknown
    ): this {
        this.#eventBus.on(event, listener, context);
        return this;
    }

    public once<T extends keyof ResourceManagerEvent>(
        event: T,
        listener: (ev: ResourceManagerEvent[T]) => unknown,
        context?: unknown
    ): this {
        this.#eventBus.once(event, listener, context);
        return this;
    }

    private constructor() {
        super();
        /* istanbul ignore if: difficult to enter */
        if (ResourceManager.#instance) {
            throw Error('ResourceManager cannot be created more than once.');
        }
    }

    public get version(): number {
        return BLUEBELL_RESOURCE_MANAGER_VERSION;
    }

    public static getInstance(): ResourceManager {
        if (!ResourceManager.#instance) {
            ResourceManager.#instance = new ResourceManager();
        }

        return ResourceManager.#instance;
    }

    public createResourceIfNotExists(uri: string, type?: ResourceType, crossOrigin?: CrossOrigin): Resource {
        this.debug('Call createResourceIfNotExists(%s, %s).', uri, type);

        const idx = uri.indexOf('#');
        if (idx !== -1) {
            uri = uri.slice(0, idx);
        }

        const foundResource = this.#resources.get(uri);

        if (foundResource) {
            this.debug('Resource with uri(%s) already exists, reuse it.', uri);
            return foundResource;
        }

        const res = Resource.create(uri, type, crossOrigin);

        this.#resources.set(uri, res);

        this.#emit('resourcecreated', { resource: res });

        return res;
    }

    public get [Symbol.toStringTag](): string {
        return 'ResourceManager';
    }

    #emit<T extends keyof ResourceManagerEvent>(event: T, payload: ResourceManagerEvent[T]): void {
        this.debug('Emit %s with %O.', event, payload);
        // Should not break down.
        try {
            this.#eventBus.emit(event, payload);
        } catch {
            //
        }
    }
}

export type { ResourceManager, Resource };

export { ResourceType };

export const getResourceManager = createGlobalFactory<ResourceManager>(
    __BLUEBELL_RESOURCE_MANAGER__,
    () => ResourceManager.getInstance(),
    BLUEBELL_RESOURCE_MANAGER_VERSION,
    // Conflict is better than throwing error.
    true
);
